import { FormEvent, useState } from 'react';
import { act, formatNumber } from '../../lib/actions';
import { api } from '../../lib/api';
import { useNova } from '../../lib/store';
import { Button } from '../ui';
import { Field, inputClass, SectionHeader, when } from './kit';

interface Found {
  numbers: string[];
  customers: { id: string; name: string | null; phone: string | null; email: string | null }[];
  calls: number;
  firstAt: string | null;
  lastAt: string | null;
  recordings: number;
  voicemails: number;
  chats?: number;
  emails?: number;
  messages?: number;
}

/** A person: an email address (chat and email) or a phone number (calls). */
const isEmail = (v: string) => v.includes('@');

/**
 * Data subject requests: a customer asks what is held about them, or asks
 * for it to be deleted. Look them up by phone number (calls) or email
 * address (chats and emails), download everything as a file, or erase it.
 */
export function Privacy() {
  const [number, setNumber] = useState('');
  const [looked, setLooked] = useState<string | null>(null);
  const [found, setFound] = useState<Found | null>(null);
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const qs = (n: string) => `${isEmail(n) ? 'email' : 'number'}=${encodeURIComponent(n.trim())}`;

  async function find(e?: FormEvent) {
    e?.preventDefault();
    setBusy(true);
    const r = await act(() => api<Found>('GET', `/privacy/subject?${qs(number)}`));
    setBusy(false);
    setFound(r ?? null);
    setLooked(r ? number : null);
    setConfirm('');
  }

  async function download() {
    if (!looked) return;
    const data = await act(() => api('GET', `/privacy/subject/export?${qs(looked)}`), 'Export downloaded');
    if (!data) return;
    const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `nova-data-${isEmail(looked) ? looked.trim().replace(/[^a-z0-9@.]/gi, '') : looked.replace(/\D/g, '')}-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function erase() {
    if (!looked) return;
    setBusy(true);
    const r = await act(
      () =>
        api<{ calls: number; recordings: number; customers: number; messages: number }>('POST', '/privacy/subject/erase', {
          ...(isEmail(looked) ? { email: looked.trim() } : { number: looked }),
          confirm,
        }),
    );
    setBusy(false);
    if (!r) return;
    const n = (k: number, one: string) => `${k} ${one}${k === 1 ? '' : 's'}`;
    useNova
      .getState()
      .toast(
        `Erased: ${n(r.customers, 'customer record')}, ${n(r.recordings, 'recording')}, ${n(r.messages ?? 0, 'message')}; ${n(r.calls, 'interaction')} kept without their details`,
        'success',
      );
    await find();
  }

  const byEmail = looked ? isEmail(looked) : false;
  const nothing = found && found.calls === 0 && !found.chats && !found.emails && found.customers.length === 0;
  // Any format of the same number: the same digits, or the same last 9; an email exactly
  const typed = confirm.replace(/\D/g, '');
  const want = looked?.replace(/\D/g, '') ?? '';
  const confirmed = byEmail
    ? confirm.trim().toLowerCase() === looked!.trim().toLowerCase()
    : typed.length >= 6 && (typed === want || (typed.length >= 9 && want.length >= 9 && typed.slice(-9) === want.slice(-9)));
  const ready = isEmail(number) ? /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(number.trim()) : number.replace(/\D/g, '').length >= 6;

  return (
    <>
      <SectionHeader
        title="Privacy requests"
        lead="When a customer asks what you hold about them, or asks you to delete it (Kenya Data Protection Act 2019 and similar laws). Look them up by phone number for calls, or by email address for chats and emails."
      />
      <form onSubmit={find} className="mb-6 flex max-w-[520px] items-end gap-2">
        <div className="flex-1">
          <Field label="Phone number or email" hint="Any phone format: 0712 345 678 and +254 712 345 678 find the same person.">
            {(id) => <input id={id} className={`${inputClass} tabular`} value={number} onChange={(e) => setNumber(e.target.value)} />}
          </Field>
        </div>
        <Button tone="primary" type="submit" className="mb-4 px-4 py-2" disabled={busy || !ready}>
          Look up
        </Button>
      </form>

      {found && looked ? (
        nothing ? (
          <p className="text-sm text-ink-2">Nothing is held about {byEmail ? looked : formatNumber(looked)}.</p>
        ) : (
          <div className="max-w-[640px] rounded-xl border border-line p-5" aria-label="What is held">
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-4">
              <div>
                <dt className="text-xs text-ink-3">Calls</dt>
                <dd className="tabular text-lg">{found.calls}</dd>
              </div>
              <div>
                <dt className="text-xs text-ink-3">Chats</dt>
                <dd className="tabular text-lg">{found.chats ?? 0}</dd>
              </div>
              <div>
                <dt className="text-xs text-ink-3">Emails</dt>
                <dd className="tabular text-lg">{found.emails ?? 0}</dd>
              </div>
              <div>
                <dt className="text-xs text-ink-3">Messages</dt>
                <dd className="tabular text-lg">{found.messages ?? 0}</dd>
              </div>
              <div>
                <dt className="text-xs text-ink-3">Recordings</dt>
                <dd className="tabular text-lg">{found.recordings}</dd>
              </div>
              <div>
                <dt className="text-xs text-ink-3">Voicemails</dt>
                <dd className="tabular text-lg">{found.voicemails}</dd>
              </div>
              <div>
                <dt className="text-xs text-ink-3">Customer records</dt>
                <dd className="tabular text-lg">{found.customers.length}</dd>
              </div>
            </dl>
            <p className="mt-4 text-sm text-ink-2">
              {found.numbers.length ? <>Numbers: {found.numbers.map((n) => formatNumber(n)).join(', ')}. </> : null}
              {found.customers.some((c) => c.name) ? <>Known as {found.customers.map((c) => c.name).filter(Boolean).join(', ')}. </> : null}
              {found.firstAt ? <>Contacts from {when(found.firstAt)} to {when(found.lastAt)}.</> : null}
            </p>

            <div className="mt-5 flex flex-wrap gap-2 border-t border-line pt-4">
              <Button onClick={() => void download()} disabled={busy}>
                Download everything (JSON)
              </Button>
            </div>

            <div className="mt-5 rounded-lg border border-break/40 p-4">
              <p className="text-sm font-medium">Erase this person</p>
              <p className="mt-1 text-xs text-ink-3">
                Deletes their customer record, recordings and voicemails (the audio too), notes and IVR answers, chat and email messages (both
                sides), and removes their number or address from calls, chats, emails, Asterisk's call log and webhook payloads. The interactions
                stay in history and reports without their details. This cannot be undone.
              </p>
              <div className="mt-3 flex flex-wrap items-end gap-2">
                <label className="flex-1 text-xs text-ink-2">
                  Type the {byEmail ? 'email address' : 'number'} again to confirm
                  <input
                    className={`${inputClass} tabular mt-1`}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    aria-label={`Type the ${byEmail ? 'email address' : 'number'} again to confirm`}
                  />
                </label>
                <Button tone="danger" className="px-4 py-2" disabled={busy || !confirmed} onClick={() => void erase()}>
                  Erase
                </Button>
              </div>
            </div>
          </div>
        )
      ) : null}
    </>
  );
}
